import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Detail, Field, Notice, Tag } from '../../../common/components/detail/detail';
import { useCommitTrace } from '../contexts/tasks';

import '../../../common/components/detail/styles/detail.css';
import '../styles/trace.css';

// From a commit to the plan it came out of.
//
// The chain is commit → task → tier → the plan version THAT FIXED THE TIER,
// with its approval, plus the authorized specification. Not "the project's
// latest approved plan": a tier points at an exact version and keeps pointing
// at it after a newer one is approved. Reading it any other way would show
// somebody a plan the work was never built from.
//
// AND WHAT IS MISSING IS SHOWN AS MISSING. The backend reports the historical
// gaps in `breaks` — a tier with no plan, a task with no authorization, a
// version with no approval — and this screen prints them instead of filling
// them in. A legacy tier has no origin, and inventing a likely one is the
// expensive mistake because it does not look like one.

const HASH = /^[0-9a-f]{7,40}$/;

export default function CommitTrace() {
    const [params, setParams] = useSearchParams();
    const submitted = (params.get('hash') || '').trim().toLowerCase();
    const [text, setText] = useState(submitted);

    const valid = HASH.test(submitted);
    const { data, isLoading, error } = useCommitTrace(submitted);

    const search = (event) => {
        event.preventDefault();
        const clean = text.trim().toLowerCase();
        const next = new URLSearchParams(params);
        if (clean) next.set('hash', clean); else next.delete('hash');
        setParams(next);
    };

    return (
        <section className="trace">
            <header className="detail-head">
                <div className="detail-head__text">
                    <h2 className="detail-head__title">De un commit a su plan</h2>
                    <p className="trace__lead">
                        Pegá el hash de un commit y el panel recorre hacia atrás: la tarea, su
                        tier, la versión de plan que fijó ese tier, y la especificación que se
                        autorizó.
                    </p>
                </div>
            </header>

            <form className="trace__form" onSubmit={search}>
                <label className="trace__field">
                    <span className="trace__label">Hash del commit</span>
                    <input
                        className="trace__input"
                        type="text"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        placeholder="7 a 40 caracteres hexadecimales"
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                    />
                </label>
                <button type="submit" className="trace__button">Buscar</button>
            </form>

            {submitted && !valid && (
                <Notice tone="warn">
                    Un hash es hexadecimal y tiene entre 7 y 40 caracteres. 40 exactos es el
                    commit completo; menos se busca como prefijo. Lo que escribiste no cumple eso,
                    así que ni se consultó.
                </Notice>
            )}

            {valid && isLoading && <p className="trace__note">Buscando...</p>}

            {valid && error && (
                <Notice tone={error.status === 404 ? 'neutral' : 'warn'}>
                    {error.status === 404
                        ? 'Ningún commit que puedas ver coincide con ese hash.'
                        : error.fields?.hash || error.message || 'No se pudo buscar el commit'}
                </Notice>
            )}

            {valid && data?.ambiguous && <Ambiguous data={data} onPick={(hash) => {
                const next = new URLSearchParams(params);
                next.set('hash', hash);
                setParams(next);
                setText(hash);
            }} />}

            {valid && data && !data.ambiguous && <Chain data={data} />}
        </section>
    );
}

// SEVERAL MATCHES AND NONE IS CHOSEN. A prefix can hit more than one repository,
// and picking the first would be picking one silently — the panel would then be
// showing a chain that belongs to a different piece of work, with nothing on
// screen to suggest it.
function Ambiguous({ data, onPick }) {
    const items = data.items ?? [];
    const total = data.pagination?.total ?? items.length;

    return (
        <section className="trace__card">
            <h3 className="trace__card-title">
                Ese prefijo coincide con {total} commits
            </h3>
            <p className="trace__note">
                No se elige ninguno: elegí vos cuál. Escribir más caracteres del hash también
                lo resuelve.
            </p>
            <ul className="trace__matches">
                {items.map(item => (
                    <li key={`${item.repository_id ?? item.repository?.id}-${item.hash}`}>
                        <button type="button" className="trace__match" onClick={() => onPick(item.hash)}>
                            <span className="trace__match-hash">{item.hash}</span>
                            <span className="trace__match-repo">
                                {item.repository?.name ?? `Repositorio #${item.repository_id}`}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
            {items.length < total && (
                <p className="trace__note">Se muestran {items.length} de {total}.</p>
            )}
        </section>
    );
}

function Chain({ data }) {
    const { commit, repository, task, tier, plan, authorization } = data;
    const breaks = data.breaks ?? [];

    return (
        <>
            {/* What a linked commit proves, and what it does not. The backend
                says it in `reading` and it is worth repeating here: a commit is
                evidence that code was tied to a task. It is not compliance, not
                acceptance, not integration and not a deploy. */}
            <Notice>
                Un commit vinculado prueba <strong>el registro</strong>: que este código quedó
                atado a esta tarea. No prueba que cumpla, ni que haya sido aceptado, ni que esté
                integrado o desplegado.
            </Notice>

            {breaks.length > 0 && (
                <Notice tone="warn">
                    <strong>Huecos en la cadena:</strong>{' '}
                    {breaks.map(item => (typeof item === 'string' ? item : item?.message ?? item?.code)).join(' · ')}.
                    {' '}No se rellenan: lo que no quedó registrado, no quedó.
                </Notice>
            )}

            <Detail>
                <Field label="Commit" value={commit?.hash} mono />
                <Field label="Repositorio" value={repository?.name ?? commit?.repository_id} />
                <Field
                    label="Tarea"
                    value={task ? (
                        <Link to={`/admin/tasks/${task.id}`}>#{task.id} · {task.title}</Link>
                    ) : null}
                    empty="Sin tarea vinculada"
                    wide
                />
                <Field
                    label="Tier"
                    value={tier ? (
                        <Link to={`/admin/tiers/${tier.id}`}>{tier.name}</Link>
                    ) : null}
                    empty="Sin tier"
                />
                <Field
                    label="Estado actual de la tarea"
                    value={task?.workflow_state}
                />
                <Field
                    label="Versión de plan que fijó el tier"
                    value={plan ? (
                        <Link to={`/admin/plans/${plan.id}${plan.version?.number != null ? `/v/${plan.version.number}` : ''}`}>
                            {plan.name ?? 'Plan'}
                            {plan.version?.number != null && ` · versión ${plan.version.number}`}
                        </Link>
                    ) : null}
                    empty="Sin origen de planeación registrado"
                    wide
                />
                <Field
                    label="Aprobación de esa versión"
                    value={plan?.approval?.approved_at
                        ? new Date(plan.approval.approved_at).toLocaleString()
                        : null}
                    empty="Sin aprobación registrada"
                />
                <Field
                    label="Autorización de la especificación"
                    value={authorization?.authorized_at
                        ? new Date(authorization.authorized_at).toLocaleString()
                        : null}
                    empty="Sin autorización registrada (tarea legada o propuesta)"
                />
            </Detail>

            {authorization?.specification && (
                <section className="trace__card">
                    <h3 className="trace__card-title">La especificación que se firmó</h3>
                    <div className="trace__tags">
                        {authorization.specification.kind && <Tag>{authorization.specification.kind}</Tag>}
                        {authorization.specification.estimate && <Tag>{authorization.specification.estimate}</Tag>}
                    </div>
                    <Detail>
                        <Field label="Título" value={authorization.specification.title} wide />
                        <Field
                            label="Descripción"
                            value={authorization.specification.description}
                            empty="Sin descripción"
                            wide
                        />
                    </Detail>
                </section>
            )}
        </>
    );
}
